import { useState } from 'react'
import { Box, Typography, useTheme } from '@mui/material'
import { couleurAutres, couleursCategories } from './palette.js'

/**
 * Anneau — ou DEMI-jauge — d'une répartition : qui pèse quoi dans le total.
 *
 * ⭐ La légende est DANS le composant, chiffrée : trois des cinq teintes sont
 * pâles sur fond blanc, l'identité d'une part ne repose donc jamais sur sa
 * seule couleur. Chaque ligne donne le nom, la part et le montant.
 *
 * ⭐ Survoler une part, ou la parcourir au clavier dans la légende, affiche SA
 * valeur au centre et estompe les autres. Rien ne se lit QUE par survol : la
 * légende porte déjà tout.
 */

export interface PartAnneau {
  cle: string
  libelle: string
  montant: number
}

/** Secteur d'anneau entre deux angles (radians, 0 = midi, sens horaire). */
function secteur(c: number, rExt: number, rInt: number, a0: number, a1: number): string {
  // ⚠️ Un tour COMPLET — une seule part, 100 % — ne se trace pas d'un arc :
  // départ et arrivée coïncident, et le navigateur ne dessine rien. On le
  // coupe en deux moitiés.
  if (a1 - a0 >= 2 * Math.PI - 1e-6) {
    const milieu = a0 + Math.PI
    return `${secteur(c, rExt, rInt, a0, milieu)} ${secteur(c, rExt, rInt, milieu, a0 + 2 * Math.PI - 1e-6)}`
  }
  const pt = (r: number, a: number): string => `${c + r * Math.sin(a)},${c - r * Math.cos(a)}`
  const grand = a1 - a0 > Math.PI ? 1 : 0
  return (
    `M${pt(rExt, a0)} A${rExt},${rExt} 0 ${grand} 1 ${pt(rExt, a1)} ` +
    `L${pt(rInt, a1)} A${rInt},${rInt} 0 ${grand} 0 ${pt(rInt, a0)} Z`
  )
}

export function GrapheAnneau({
  titre,
  parts,
  formater,
  libelleTotal,
  demi = false,
  taille = 168,
}: {
  titre: string
  /** Déjà repliées : cinq au plus, « Autres » en dernier le cas échéant. */
  parts: readonly PartAnneau[]
  formater: (v: number) => string
  /** Le mot sous le total, au centre : « Total », « Encaissé »… */
  libelleTotal: string
  /** Demi-jauge : l'arc du haut seulement, la légende dessous. */
  demi?: boolean
  taille?: number
}): JSX.Element {
  const theme = useTheme()
  const sombre = theme.palette.mode === 'dark'
  const couleurs = couleursCategories(sombre)
  const [actif, setActif] = useState<string | null>(null)

  const total = parts.reduce((s, p) => s + p.montant, 0)
  const couleurDe = (p: PartAnneau, i: number): string =>
    p.cle === 'autres' ? couleurAutres(sombre) : (couleurs[i] ?? couleurAutres(sombre))

  const c = taille / 2
  const rExt = c - 2
  const rInt = rExt * (demi ? 0.72 : 0.66)
  const debut = demi ? -Math.PI / 2 : 0
  const etendue = demi ? Math.PI : 2 * Math.PI
  // L'écart de 2 px entre deux parts, converti en angle au rayon extérieur.
  const ecart = parts.length > 1 ? 2 / rExt : 0

  let angle = debut
  const secteurs = parts.map((p, i) => {
    const a = total > 0 ? (p.montant / total) * etendue : 0
    const s = { p, i, a0: angle + ecart / 2, a1: angle + a - ecart / 2 }
    angle += a
    return s
  })

  const partActive = parts.find((p) => p.cle === actif) ?? null
  const hauteurSvg = demi ? c + 4 : taille
  const pourcent = (v: number): string => (total > 0 ? `${Math.round((v / total) * 100)} %` : '—')

  if (total <= 0) {
    return (
      <Box sx={{ height: demi ? c + 60 : taille, display: 'grid', placeItems: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Aucune donnée sur la période.
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        display: 'flex',
        // La légende SOUS l'anneau : à côté, dans une carte d'un tiers de
        // largeur, les noms de catégorie se tronquaient en « Sa… ».
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
      }}
    >
      <Box sx={{ position: 'relative', width: taille, height: hauteurSvg, flexShrink: 0 }}>
        <svg
          width={taille}
          height={hauteurSvg}
          role="img"
          aria-label={`${titre} : ${parts.map((p) => `${p.libelle} ${pourcent(p.montant)}`).join(', ')}`}
          onPointerLeave={() => setActif(null)}
        >
          {demi ? (
            // La piste de la jauge, un cran plus clair que la surface.
            <path
              d={secteur(c, rExt, rInt, debut, debut + etendue)}
              fill={theme.palette.action.hover}
            />
          ) : null}
          {secteurs.map(({ p, i, a0, a1 }) =>
            a1 > a0 ? (
              <path
                key={p.cle}
                d={secteur(c, rExt, rInt, a0, a1)}
                fill={couleurDe(p, i)}
                opacity={actif !== null && actif !== p.cle ? 0.35 : 1}
                onPointerEnter={() => setActif(p.cle)}
                onPointerDown={() => setActif(p.cle)}
                style={{ cursor: 'default' }}
              />
            ) : null,
          )}
        </svg>
        {/* Le centre : le total, ou la part visée. */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            ...(demi ? { bottom: 0 } : { top: '50%', transform: 'translateY(-50%)' }),
            textAlign: 'center',
            pointerEvents: 'none',
            px: 3,
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }} noWrap>
            {partActive ? partActive.libelle : libelleTotal}
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }} noWrap>
            {formater(partActive ? partActive.montant : total)}
          </Typography>
        </Box>
      </Box>

      {/* La légende : l'identité ET la valeur de chaque part, sans survol. */}
      <Box
        component="ul"
        sx={{ listStyle: 'none', m: 0, p: 0, width: '100%', display: 'grid', gap: 1 }}
      >
        {parts.map((p, i) => (
          <Box
            component="li"
            key={p.cle}
            tabIndex={0}
            onFocus={() => setActif(p.cle)}
            onBlur={() => setActif(null)}
            onPointerEnter={() => setActif(p.cle)}
            onPointerLeave={() => setActif(null)}
            sx={{
              display: 'grid',
              gridTemplateColumns: '10px 1fr auto auto',
              alignItems: 'center',
              gap: 1.5,
              px: 1,
              py: 0.5,
              borderRadius: 1,
              outline: 'none',
              bgcolor: actif === p.cle ? 'action.hover' : 'transparent',
              '&:focus-visible': { boxShadow: `0 0 0 2px ${theme.palette.primary.main}` },
            }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: couleurDe(p, i) }} />
            <Typography variant="body2" noWrap title={p.libelle}>
              {p.libelle}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
            >
              {pourcent(p.montant)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                textAlign: 'right',
                minWidth: 72,
              }}
            >
              {formater(p.montant)}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
